import "./PromptInput.scss";
import cn from "classnames";
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { MdArrowUpward, MdOutlineManageSearch, MdStop } from "react-icons/md";
import { Button, ButtonType } from "../../Components/Button/Button";
import { ButtonFrameworkVariant } from "../../Components/ButtonFramework/ButtonFramework";
import { MenuItem } from "../../Components/Dropdown/Dropdown";
import { DropdownMenu } from "../../Components/DropdownMenu/DropdownMenu";
import { OverlayTrigger } from "../../Components/OverlayTrigger";
import { useSetError } from "../../redux/modals";
import { getPromptLibrary } from "../LLMConstants";

export interface PromptInputProps {
  isOpen: boolean;
  sendPrompt: (message: string) => void;
  submitting: boolean;
  draftMessage: string;
  setDraftMessage: Dispatch<SetStateAction<string>>;
}

// Hide interrupt button until DEV-20087 is unblocked
const INTERRUPT_AVAILABLE = false;

export const PromptInput = ({
  isOpen,
  sendPrompt,
  submitting,
  draftMessage,
  setDraftMessage,
}: PromptInputProps): JSX.Element => {
  const [showPromptLibraryMenu, setShowPromptLibraryMenu] = useState(false);
  const promptMenuRef = useRef<HTMLDivElement | null>(null);
  const promptInputRef = useRef<HTMLTextAreaElement | null>(null);
  const setError = useSetError();

  const submit = useCallback(() => {
    if (draftMessage && !submitting) {
      sendPrompt(draftMessage);
      setDraftMessage("");
    }
  }, [draftMessage, sendPrompt, setDraftMessage, submitting]);
  const interrupt = useCallback(() => setError({ message: "Interrupt not yet implemented" }), [
    setError,
  ]);

  const submitBtnProps = useMemo(
    () => ({
      onClick: submit,
      icon: <MdArrowUpward />,
      className: "submit",
      disabled: submitting || !draftMessage,
    }),
    [draftMessage, submit, submitting]
  );

  const submitOrInterruptBtnProps = useMemo(
    () => ({
      onClick: submitting ? interrupt : submit,
      icon: submitting ? <MdStop /> : <MdArrowUpward />,
      className: cn({ submit: !submitting, interrupt: submitting }),
      disabled: !submitting && !draftMessage,
    }),
    [draftMessage, interrupt, submit, submitting]
  );

  const menuItems: MenuItem<string>[] = useMemo(() => {
    const promptLibrary = getPromptLibrary(true);
    return promptLibrary.map(({ label, value }) => ({
      label,
      value,
      disabled: submitting,
    }));
  }, [submitting]);

  useEffect(() => {
    if (isOpen) {
      promptInputRef?.current?.focus();
    }
  }, [isOpen]);

  return (
    <div className="promptInputContainer">
      <div className="promptInput">
        <Form.Control
          id="prompt"
          as="textarea"
          rows={3}
          placeholder="Ask about your data..."
          value={draftMessage}
          onChange={e => setDraftMessage(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter") {
              submit();
              e.preventDefault();
            }
          }}
          ref={promptInputRef}
        />
        <div className="promptButtonsContainer">
          <div className="promptButtons" ref={promptMenuRef}>
            <OverlayTrigger
              placement={OverlayTrigger.PLACEMENTS.TOP.LEFT}
              trigger="click"
              exitOnBackgroundClick
              loseFocusOnScroll={false}
              backgroundClassName="transparent"
              onExit={() => setShowPromptLibraryMenu(false)}
              customTrigger={showPromptLibraryMenu}
              overlay={
                // We have to use an overlay so that the DropdownMenu's base element in higher in the DOM tree
                // and is therefore able to escape the overflow: hidden property of the FloatingPanel
                <DropdownMenu
                  size="sm"
                  standalone
                  hasFilterBar
                  className="promptLibraryMenu"
                  menuItems={menuItems}
                  itemOnClick={item => {
                    setDraftMessage(item.value);
                    setShowPromptLibraryMenu(false);
                  }}
                  filterBarPosition="bottom"
                />
              }
            >
              <Button
                type={ButtonType.EMPTY}
                variant={ButtonFrameworkVariant.LEADING_ICON}
                icon={<MdOutlineManageSearch />}
                design="secondary"
                size="sm"
                className="promptLibraryBtn"
                onClick={() => setShowPromptLibraryMenu(true)}
              >
                Prompt Library
              </Button>
            </OverlayTrigger>
            <Button
              type={ButtonType.FILLED}
              variant={ButtonFrameworkVariant.ICON_ONLY}
              {...(INTERRUPT_AVAILABLE ? submitOrInterruptBtnProps : submitBtnProps)}
            />
          </div>
        </div>
      </div>
      <div className="disclaimer">AI generated content. Please verify for accuracy.</div>
    </div>
  );
};
